import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Apiservice } from '../../../../../services/ApiService';
import { ApiVenue, VenueContext, VenueListExtended } from '../../../../../models/venue-admin.model';

type VenueType = 'parent' | 'standard';

const ALL_CONTEXTS: VenueContext[] = ['scan', 'report', 'feed', 'ticketTypeReport'];
const CONTINENTS: string[] = [
  'Africa',
  'Asia',
  'Australia',
  'Europe',
  'New Zealand',
  'North America',
  'South America'
];
const PARENT_NAME_MARKER = '-parent';
const CONNECTOR_YAML_URL =
  'https://git.tmaws.io/kafka-engineering/kafka-cluster/kafka-connect/connector-management/-/blob/main/prod/us-east-1/connectors/prd3330-apac-attendance-s3-sink-prod.yaml?ref_type=heads';

@Component({
  selector: 'app-add-venue',
  templateUrl: './add-venue.component.html',
  standalone: false,
  styleUrls: ['./add-venue.component.scss']
})
export class AddVenueComponent {
  @Input() record: VenueListExtended | null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  readonly allContexts = ALL_CONTEXTS;
  readonly continents = CONTINENTS;
  readonly timezones: string[] = this.loadTimezones();
  readonly connectorYamlUrl = CONNECTOR_YAML_URL;
  connectorSnippetCopied = false;

  type: VenueType = 'standard';
  id = '';
  name = '';
  timezone = '';
  continent = '';
  city = '';
  context: Record<VenueContext, boolean> = {
    scan: true,
    report: true,
    feed: true,
    ticketTypeReport: false
  };
  parents: string[] = [];

  step: 'form' | 'confirm' = 'form';
  saving = false;
  validationError = '';
  saveError = '';

  constructor(private apiservice: Apiservice) {}

  get parentOptions(): Array<{ id: string; name: string }> {
    const venues = this.record?.venues ?? {};
    return Object.keys(venues)
      .filter((id) => {
        const name = (venues[id].name || '').toLowerCase();
        return id.toLowerCase().includes(PARENT_NAME_MARKER) || name.includes(PARENT_NAME_MARKER);
      })
      .map((id) => ({ id, name: venues[id].name || id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get selectedContexts(): VenueContext[] {
    if (this.type === 'parent') { return ['report']; }
    return this.allContexts.filter((c) => this.context[c]);
  }

  get connectorSnippet(): string {
    const newId = this.id.trim();
    if (!newId) { return ''; }
    return `|| @.id =~ /${newId}/`;
  }

  copyConnectorSnippet() {
    navigator.clipboard.writeText(this.connectorSnippet).then(() => {
      this.connectorSnippetCopied = true;
      setTimeout(() => { this.connectorSnippetCopied = false; }, 2000);
    });
  }

  onTypeChange(type: VenueType) {
    this.type = type;
    if (type === 'parent') {
      this.parents = [];
    }
    this.validationError = '';
  }

  onContextToggle(ctx: VenueContext, checked: boolean) {
    this.context[ctx] = checked;
  }

  onParentToggle(id: string, checked: boolean) {
    if (checked) {
      if (!this.parents.includes(id)) { this.parents = [...this.parents, id]; }
    } else {
      this.parents = this.parents.filter((p) => p !== id);
    }
  }

  proceedToConfirm() {
    this.validationError = this.validate();
    if (this.validationError) { return; }
    this.step = 'confirm';
  }

  backToForm() {
    this.step = 'form';
    this.saveError = '';
  }

  cancel() {
    this.cancelled.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget && !this.saving) {
      this.cancel();
    }
  }

  confirmSave() {
    if (!this.record) { return; }
    this.saving = true;
    this.saveError = '';

    const newVenue: ApiVenue = {
      name: this.name.trim(),
      timezone: this.timezone.trim() || undefined,
      context: this.selectedContexts,
      parents: this.type === 'parent' ? [] : [...this.parents],
      Continent: this.continent.trim() || undefined,
      City: this.city.trim() || undefined
    };

    const mergedVenues: Record<string, ApiVenue> = {
      ...this.record.venues,
      [this.id.trim()]: newVenue
    };

    this.apiservice.postVenueAdmin({
      primaryVenue: this.record.primaryVenue,
      venues: mergedVenues
    }).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit();
      },
      error: (err) => {
        this.saving = false;
        this.saveError = typeof err?.error === 'string'
          ? err.error
          : (err?.message || 'Failed to save venue. Please try again.');
      }
    });
  }

  private validate(): string {
    const id = this.id.trim();
    const name = this.name.trim();

    if (!id) { return 'Venue ID is required.'; }
    if (!name) { return 'Name is required.'; }

    const existing = this.record?.venues ?? {};
    if (Object.prototype.hasOwnProperty.call(existing, id)) {
      return `Venue ID '${id}' already exists.`;
    }

    const hasParentMarker = name.toLowerCase().includes(PARENT_NAME_MARKER);
    if (this.type === 'parent' && !hasParentMarker) {
      return "Parent venue name must include '-parent'.";
    }
    if (this.type === 'standard' && hasParentMarker) {
      return "Standard venue name must not include '-parent'.";
    }

    if (this.type === 'standard' && this.selectedContexts.length === 0) {
      return 'Select at least one context.';
    }

    return '';
  }

  private loadTimezones(): string[] {
    const intlAny = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
    if (typeof intlAny.supportedValuesOf === 'function') {
      try {
        return intlAny.supportedValuesOf('timeZone');
      } catch {
        return [];
      }
    }
    return [];
  }
}
